import React from 'react';
import { Trash2 } from 'lucide-react';
import { NotificationProvider } from '../../../types/domain';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';

interface ProviderCardProps {
  provider: NotificationProvider;
  cardTest?: { success: boolean; message: string };
  isTesting: boolean;
  canAdmin: boolean;
  onTest: () => void;
  onDelete: () => void;
}

export const ProviderCard: React.FC<ProviderCardProps> = ({
  provider,
  cardTest,
  isTesting,
  canAdmin,
  onTest,
  onDelete,
}) => {
  return (
    <Card className="p-4 bg-cf-surface border-cf-border flex flex-col justify-between hover:border-cf-borderActive transition-colors">
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-xs text-cf-textPrimary">{provider.name}</span>
          </div>

          <Badge variant="default" size="xs">
            {provider.provider_type}
          </Badge>
        </div>

        <div className="text-xs text-cf-textSecondary space-y-1.5 mt-3">
          {provider.provider_type === 'smtp' && (
            <>
              <div>
                Host: <span className="text-cf-textPrimary">{provider.config.host}:{provider.config.port}</span>
              </div>
              <div>
                Encryption: <span className="text-cf-textPrimary">{provider.config.mode || 'none'}</span>
              </div>
              <div>
                From: <span className="text-cf-textPrimary">{provider.config.from_address || 'Default'}</span>
              </div>
              {provider.config.username && (
                <div>
                  User: <span className="text-cf-textSecondary">{provider.config.username}</span>
                </div>
              )}
            </>
          )}
          {provider.provider_type === 'resend' && (
            <>
              <div>
                API Key: <span className="text-cf-textPrimary">re_••••••••</span>
              </div>
              <div>
                From: <span className="text-cf-textPrimary">{provider.config.from_address}</span>
              </div>
            </>
          )}
        </div>

        {/* Card Test Status */}
        {cardTest && (
          <div
            className={`mt-3 p-2 rounded text-xs border font-medium ${
              cardTest.success
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
                : 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300'
            }`}
          >
            {cardTest.success ? '✓ ' : '✕ '} {cardTest.message}
          </div>
        )}
      </div>

      <div className="mt-4 pt-3 border-t border-cf-border flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
            <span>Ready</span>
          </span>

          {provider.provider_type === 'smtp' && (
            <Button
              variant="secondary"
              size="xs"
              onClick={onTest}
              disabled={isTesting}
              isLoading={isTesting}
            >
              {isTesting ? 'Pinging...' : 'Test Ping'}
            </Button>
          )}
        </div>

        {canAdmin && (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={onDelete}
            className="text-cf-textMuted hover:text-rose-400"
            title="Delete provider"
          >
            <Trash2 size={13} />
          </Button>
        )}
      </div>
    </Card>
  );
};
